import { useCallback, useMemo, useState } from 'react'
import { Pencil, Plus, Tag, EyeOff } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import {
  fetchMarketCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  type AdminCategoryRow,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useToast } from '@/providers/UiProviders'

type EditTarget = AdminCategoryRow | 'new' | null

/**
 * A21.01 — Bozor kategoriyalari (spec-29 §2). GET /admin/market/categories;
 * qo'shish/tahrir → POST/PUT; o'chirish = soft-disable (enabled=false, mavjud
 * e'lonlar referensni saqlaydi). Kategoriya = admin belgilaydi; sotuvchi tanlaydi.
 */
export function Categories() {
  const t = useT()
  const { toast } = useToast()
  const [target, setTarget] = useState<EditTarget>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.market.done, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(t.market.failed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
        throw e
      }
    },
    [toast, t],
  )

  const columns = useMemo<AdminColumn<AdminCategoryRow>[]>(
    () => [
      {
        key: 'title',
        header: t.market.catTitle,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <span className="flex items-center gap-2">
            <Tag size={14} className="text-tertiary" />
            <span className="font-medium text-primary">{r.title}</span>
            {r.icon ? <span className="font-mono text-xs text-tertiary">{r.icon}</span> : null}
          </span>
        ),
      },
      { key: 'position', header: t.market.catPosition, type: 'number', width: 100, align: 'right' },
      { key: 'listings', header: t.market.catListings, type: 'number', width: 110, align: 'right' },
      {
        key: 'enabled',
        header: t.market.catEnabled,
        type: 'text',
        width: 120,
        render: (r) => <StatusBadge status={r.enabled ? 'active' : 'deleted'} label={r.enabled ? t.market.catEnabled : t.market.catDisable} />,
      },
    ],
    [t],
  )

  const rowActions = useMemo<RowAction<AdminCategoryRow>[]>(
    () => [
      { id: 'edit', label: t.market.editCategory, icon: <Pencil size={13} />, onSelect: (r) => setTarget(r) },
      {
        id: 'disable',
        label: t.market.catDisable,
        icon: <EyeOff size={13} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: (r) => void run(() => deleteCategory(r.id)),
      },
    ],
    [t, run],
  )

  return (
    <>
      <PageHeader
        title={t.market.categories}
        screenId="MKT.02"
        actions={
          <Button size="sm" onClick={() => setTarget('new')}>
            <Plus size={14} /> {t.market.addCategory}
          </Button>
        }
      />
      <DataTable<AdminCategoryRow>
        key={reloadKey}
        screenId="MKT.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchMarketCategories}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Tag size={22} />, title: t.market.categories }}
      />

      {target && (
        <CategoryDialog
          target={target}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </>
  )
}

/** Create/edit category modal. */
function CategoryDialog({ target, onClose, onSaved }: { target: EditTarget; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const { toast } = useToast()
  const editing = target !== 'new' && target !== null ? target : null
  const [title, setTitle] = useState(editing?.title ?? '')
  const [icon, setIcon] = useState(editing?.icon ?? '')
  const [position, setPosition] = useState(String(editing?.position ?? 0))
  const [enabled, setEnabled] = useState(editing?.enabled ?? true)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!title.trim()) return
    setBusy(true)
    try {
      const pos = parseInt(position, 10) || 0
      if (editing) {
        await updateCategory(editing.id, { title: title.trim(), icon: icon.trim(), position: pos, enabled })
      } else {
        await createCategory({ title: title.trim(), icon: icon.trim(), parent_id: 0, position: pos })
      }
      toast(t.market.done, { tone: 'success' })
      onSaved()
    } catch (e) {
      setBusy(false)
      toast(t.market.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? t.market.editCategory : t.market.newCategory}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.market.cancel}
          </Button>
          <Button onClick={save} disabled={busy || !title.trim()}>
            {t.market.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">{t.market.catTitle}</span>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">{t.market.catIcon}</span>
          <Input value={icon} onChange={(e) => setIcon(e.target.value)} mono placeholder="devices" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">{t.market.catPosition}</span>
          <Input value={position} onChange={(e) => setPosition(e.target.value)} inputMode="numeric" />
        </label>
        {editing && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="text-secondary">{t.market.catEnabled}</span>
          </label>
        )}
      </div>
    </Dialog>
  )
}
